import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConVersion } from '../../api/cliente';
import type { Canal, Configuracion, ConfiguracionEscrita, EstadoAsistente } from '../../api/ops/asistente';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoNoContratado, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { rolesDe } from '../../logica/acceso';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import type { DatosAsistente } from './comun';
import { Configuracion as ApartadoConfiguracion } from './Configuracion';
import { Instrucciones, Preguntas, SinAsistente } from './LoQueSabe';
import { Probar } from './Probar';
import { apartadoDe, APARTADOS, autorDe, ayudaDelInterruptor, chipDeEstado, escritura, fechaCorta, notaDeEstado, usoDe, type Apartado, type Equipo, type Uso } from './reglas';
import { SOLO_LECTURA } from './textos';
import '../../componentes/apartado.css';
import '../avisos/avisos.css';
import './asistente.css';

/**
 * «Asistente» (Configuración, 11.5 a 11.7): arriba, la cabecera del asistente (`getAssistant`) con el interruptor general
 * (`setAssistantEnabled`, al momento y fuera de las versiones, con «Deshacer») y el consumo del mes (`getUsage`, concepto
 * `conversations`; G23, G24, G452). Debajo, una sola barra de apartados en `?apartado=`: Configuración · Preguntas ·
 * Instrucciones · Sin asistente · Probar y publicar. Lo de Configuración, Preguntas e Instrucciones va al borrador
 * (`updateAssistantDraft`, se guarda solo); los contactos sin asistente, al momento. La pantalla se registra con la clave
 * `asistente`, la entrada del menú que el manifiesto declara con `asistente.configuracion.editar` (G684, icono `assistant`).
 */
const COMPONENTES = { configuracion: ApartadoConfiguracion, preguntas: Preguntas, instrucciones: Instrucciones, 'sin-asistente': SinAsistente, probar: Probar } as const;

const ESPERA_GUARDADO_MS = 600;

/** Si se ha escrito algo más mientras se guardaba (se lee la referencia, que cambia fuera de este flujo). */
const hayPendiente = (r: { current: unknown }) => r.current !== null;

interface Cargado {
    estado: ConVersion<EstadoAsistente>;
    borrador: ConVersion<Configuracion>;
    publicada: Configuracion | null;
}

export function Asistente(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, navegar, busqueda, ruta, membresia, nombreNegocio } = props;
    const { puede } = usePermisos();
    const avisar = useAvisoBreve();
    const [cargado, setCargado] = useState<Cargado | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [canales, setCanales] = useState<Canal[] | null>(null);
    const [uso, setUso] = useState<Uso | null>(null);
    const [excluidos, setExcluidos] = useState<number | null>(null);
    const [equipo, setEquipo] = useState<Equipo | null>(null);
    const [caducidad, setCaducidad] = useState<number | null>(null);
    const [prueba, setPrueba] = useState<string | null>(null);
    const [guardando, setGuardando] = useState(false);
    const [deshacer, setDeshacer] = useState<{ clave: number; encendido: boolean } | null>(null);
    const [sinVersion, setSinVersion] = useState(false);
    const pendiente = useRef<ConfiguracionEscrita | null>(null);
    const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
    const enCurso = useRef(false);
    const actual = useRef<Cargado | null>(null);
    useEffect(() => {
        actual.current = cargado;
    }, [cargado]);

    const editarPermiso = puede('asistente.configuracion.editar');
    const probarPermiso = puede('asistente.configuracion.publicar');
    const verPlan = puede('plan.ver');
    const verBuzon = puede('canales.buzon.configurar');
    const verConversaciones = puede('canales.conversaciones.ver');
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!editarPermiso) {
            return;
        }
        let vivo = true;
        Promise.all([ops.getAssistant(negocio), ops.getAssistantDraft(negocio)])
            .then(async ([estado, borrador]) => {
                const pub = estado.datos.published_version ?? null;
                const publicada = pub === null ? null : await ops.getAssistantVersion(negocio, pub.id).catch(() => null);
                if (vivo) {
                    setCargado({ estado, borrador, publicada });
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        ops.listChannels(negocio).then((r) => { if (vivo) { setCanales(r.data); } }).catch(() => { if (vivo) { setCanales(null); } });
        // El consumo es de Planes (`getUsage`, `plan.ver`): sin ese permiso no se enseña.
        if (verPlan) {
            ops.getUsage(negocio).then((r) => { if (vivo) { setUso(usoDe(r.data)); } }).catch(() => undefined);
        }
        // Quién hizo cada cosa (G719): sin permiso para ver el equipo, el nombre guardado tal cual.
        ops.listMembers(negocio, { limit: 100 })
            .then((r) => { if (vivo) { setEquipo({ nombres: new Map(r.data.map((m) => [m.id, m.display_name])), completo: r.next_cursor == null }); } })
            .catch(() => undefined);
        ops.listAssistantExcludedContacts(negocio, { limit: 100 }).then((r) => { if (vivo) { setExcluidos(r.data.length); } }).catch(() => undefined);
        // La caducidad del modo humano es un ajuste del buzón (G689): aquí solo se enseña, con `canales.buzon.configurar`.
        if (verBuzon) {
            ops.getInboxSettings(negocio).then((r) => { if (vivo) { setCaducidad(r.datos.human_mode_expiry_hours); } }).catch(() => undefined);
        }
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento, editarPermiso, verPlan, verBuzon]);

    /** Guarda el último borrador pedido; si mientras tanto hubo otro cambio, lo guarda después (uno cada vez). */
    const guardar = useCallback(async () => {
        if (enCurso.current) {
            return;
        }
        enCurso.current = true;
        setGuardando(true);
        let cambio = pendiente.current;
        while (cambio !== null) {
            pendiente.current = null;
            const c = actual.current;
            if (c === null || cambio.assistant_name.trim() === '') {
                break;
            }
            try {
                const r = await ops.updateAssistantDraft(negocio, escritura(cambio), c.borrador.version ?? '');
                const estado = await ops.getAssistant(negocio).catch(() => null);
                const otroCambio = hayPendiente(pendiente);
                // La versión nueva, ya para el siguiente guardado; lo escrito mientras tanto no se pisa.
                actual.current = { ...c, borrador: { ...c.borrador, version: r.version } };
                setCargado((x) =>
                    x === null
                        ? x
                        : {
                              ...x,
                              estado: estado ?? x.estado,
                              borrador: { version: r.version, datos: otroCambio ? { ...x.borrador.datos, instruction_warnings: r.datos.instruction_warnings, version: r.datos.version } : r.datos },
                          },
                );
            } catch (e) {
                pendiente.current = null;
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    avisar({ tipo: 'error', texto: 'Otra persona ha cambiado el borrador. Te enseñamos lo último.' });
                    recargar();
                } else {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido guardar el borrador. Inténtalo otra vez.' });
                }
            }
            cambio = pendiente.current;
        }
        enCurso.current = false;
        setGuardando(false);
    }, [ops, negocio, avisar, recargar]);

    const cambiar = useCallback(
        (f: (c: ConfiguracionEscrita) => ConfiguracionEscrita) => {
            setCargado((c) => {
                if (c === null) {
                    return c;
                }
                const nuevo = { ...c.borrador.datos, ...f(escritura(c.borrador.datos)) };
                pendiente.current = escritura(nuevo);
                return { ...c, borrador: { ...c.borrador, datos: nuevo } };
            });
            if (temporizador.current !== null) {
                clearTimeout(temporizador.current);
            }
            temporizador.current = setTimeout(() => {
                void guardar();
            }, ESPERA_GUARDADO_MS);
        },
        [guardar],
    );

    useEffect(
        () => () => {
            if (temporizador.current !== null) {
                clearTimeout(temporizador.current);
            }
        },
        [],
    );

    const nuevoBorrador = useCallback(
        (b: ConVersion<Configuracion>) => {
            pendiente.current = null;
            setCargado((c) => (c === null ? c : { ...c, borrador: b }));
            ops.getAssistant(negocio)
                .then((estado) => { setCargado((c) => (c === null ? c : { ...c, estado })); })
                .catch(() => undefined);
        },
        [ops, negocio],
    );

    const encender = useCallback(
        async (encendido: boolean, conDeshacer: boolean) => {
            const c = actual.current;
            if (c === null) {
                return;
            }
            try {
                const estado = await ops.setAssistantEnabled(negocio, encendido, c.estado.version ?? '');
                setCargado((x) => (x === null ? x : { ...x, estado }));
                if (conDeshacer) {
                    setDeshacer({ clave: Date.now(), encendido: !encendido });
                }
            } catch (e) {
                if (e instanceof ErrorApi && e.codigo === 'conflict' && encendido) {
                    setSinVersion(true);
                } else if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    recargar();
                } else {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido cambiar. Inténtalo otra vez.' });
                }
            }
        },
        [ops, negocio, avisar, recargar],
    );

    const sinPermiso = <EstadoSinPermiso seccion="Asistente" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    if (!editarPermiso) {
        return sinPermiso;
    }
    if (error !== null) {
        if (error instanceof ErrorApi && error.codigo === 'entitlement_required') {
            return <EstadoNoContratado bloque="Asistente IA" incluye={[]} propietario={membresia?.is_owner === true} onAnadir={membresia?.is_owner === true ? () => { navegar('/plan'); } : null} onInicio={() => { navegar('/'); }} movil={movil} />;
        }
        if (error instanceof ErrorApi && error.codigo === 'permission_denied') {
            return sinPermiso;
        }
        return <EstadoError seccion="tu asistente" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (cargado === null) {
        return <EstadoCargando seccion="tu asistente" />;
    }

    const apartado = apartadoDe(busqueda.get('apartado'));
    const base = ruta.split('?')[0] ?? ruta;
    const irA = (a: Apartado) => { navegar(rutaConApartado(base, a)); };
    const estado = cargado.estado.datos;
    const publicada = estado.published_version ?? null;
    const editar = !soloLectura && editarPermiso;
    const datos: DatosAsistente = {
        estado,
        borrador: cargado.borrador,
        publicada: cargado.publicada,
        canales,
        editar,
        probar: probarPermiso,
        publicar: probarPermiso && !soloLectura,
        guardando,
        cambiar,
        nuevoBorrador,
        recargar,
        irA,
        fijarExcluidos: setExcluidos,
        caducidad,
        pruebaDelBorrador: prueba,
        fijarPruebaDelBorrador: setPrueba,
        equipo,
    };
    const propietario = membresia?.is_owner === true;
    const nota = notaDeEstado(estado.status, uso, propietario);
    const chip = chipDeEstado(estado.status);
    const nombre = cargado.publicada?.assistant_name ?? (cargado.borrador.datos.assistant_name === '' ? 'Tu asistente' : cargado.borrador.datos.assistant_name);
    const nombreVacio = cargado.borrador.datos.assistant_name.trim() === '';
    const cambiosSinPublicar = publicada !== null && estado.has_unpublished_changes;
    const guardado = nombreVacio
        ? 'Falta el nombre: el borrador no se guarda'
        : guardando
          ? 'Guardando en el borrador…'
          : publicada === null
            ? 'Borrador guardado'
            : cambiosSinPublicar
              ? movil
                  ? 'Borrador guardado'
                  : `Borrador guardado · tus clientes siguen con la versión ${String(publicada.number)}`
              : 'Todo publicado';
    const encendido = publicada !== null && estado.enabled;
    const cifras: Partial<Record<Apartado, number | null>> = {
        instrucciones: (cargado.borrador.datos.instruction_warnings ?? []).length || null,
        'sin-asistente': excluidos === 0 ? null : excluidos,
    };
    const Apartado = COMPONENTES[apartado];
    const publicadaPor = publicada === null ? null : autorDe(publicada.published_by_member_id, publicada.published_by_name, equipo);
    const cambiadoPor = autorDe(estado.enabled_changed_by_member_id, estado.enabled_changed_by_name, equipo);
    // «Pasadas al equipo» (G718, G753): las cifras del mes las da el Asistente (`handed_to_team` y `conversations_total`); el
    // panel solo calcula el porcentaje. La comparación con el mes anterior es del indicador de Analítica, no de esta tarjeta.
    const pasadas = estado.handed_to_team ?? null;
    const atendidas = estado.conversations_total ?? null;

    const boton = (b: 'probar' | 'buzon' | 'conversaciones') =>
        b === 'probar' ? (
            apartado === 'probar' ? null : (
                <Boton key={b} tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { irA('probar'); }}>
                    Probar y publicar
                </Boton>
            )
        ) : b === 'buzon' ? (
            <Boton key={b} tipo="secundario" icono="inbox" movil={movil} onClick={() => { navegar('/buzon'); }}>
                Ir al Buzón
            </Boton>
        ) : (
            <Boton key={b} tipo="secundario" icono="plus" movil={movil} onClick={() => { navegar('/plan?apartado=consumo'); }}>
                Añadir conversaciones
            </Boton>
        );

    return (
        <div className="rv-en-pantalla rv-as">
            <div className="rv-as-cabeza">
                <section className="rv-as-tarjeta rv-as-quien" aria-label="Tu asistente">
                    <div className="rv-as-quien__fila">
                        <div className="rv-as-quien__id">
                            <span className={`rv-as-baldosa${estado.status === 'attending' ? ' rv-as-baldosa--roja' : ''}`} aria-hidden="true">
                                <Icono nombre="bot" />
                            </span>
                            <span className="rv-as-quien__texto">
                                <span className="rv-as-quien__nombre">
                                    <strong>{nombre}</strong>
                                    {chip !== null && <Chip estado={chip.estado}>{chip.texto}</Chip>}
                                    {cambiosSinPublicar && <Chip estado="pending">Cambios sin publicar</Chip>}
                                </span>
                                <span>{publicada === null ? 'Todavía no has publicado ninguna versión.' : movil ? `Versión ${String(publicada.number)} · ${fechaCorta(publicada.published_at)}` : `Versión ${String(publicada.number)} · publicada el ${fechaCorta(publicada.published_at)}${publicadaPor === null ? '' : ` por ${publicadaPor}`}`}</span>
                                <span className={`rv-as-guardado${nombreVacio ? ' rv-as-guardado--mal' : ''}`} role="status">
                                    <Icono nombre={nombreVacio ? 'circle-alert' : 'check'} />
                                    {guardado}
                                </span>
                            </span>
                        </div>
                        <div className="rv-as-interruptor">
                            <span>
                                <strong>Encendido</strong>
                                {!movil && <small>{ayudaDelInterruptor(publicada !== null, estado.enabled)}</small>}
                                {!movil && cambiadoPor !== null && estado.enabled_changed_at != null && <small>{`${estado.enabled ? 'Encendido' : 'Apagado'} por ${cambiadoPor} · ${fechaCorta(estado.enabled_changed_at)}`}</small>}
                            </span>
                            <Interruptor
                                ariaLabel="Asistente encendido"
                                valor={encendido}
                                linea="ninguna"
                                desactivado={!editarPermiso}
                                onChange={(v) => {
                                    if (publicada === null && v) {
                                        setSinVersion(true);
                                        return;
                                    }
                                    void encender(v, true);
                                }}
                            />
                        </div>
                    </div>
                    {nota !== null && (
                        <div className={`rv-as-nota rv-as-nota--${nota.tono}`}>
                            <span>
                                <Icono nombre={nota.icono} />
                                <span>{nota.texto}</span>
                            </span>
                            {nota.botones.map(boton)}
                        </div>
                    )}
                </section>
                {((uso !== null && publicada !== null) || pasadas !== null) && (
                <div className="rv-as-cifras">
                {uso !== null && publicada !== null && (
                    <button type="button" className="rv-as-tarjeta rv-as-uso" aria-label={`Conversaciones: ${uso.detalle} este mes. Ver el consumo`} onClick={() => { navegar('/plan?apartado=consumo'); }}>
                        <Anillo porcentaje={uso.porcentaje} />
                        <span className="rv-as-uso__texto">
                            <strong>
                                {uso.porcentaje >= 90 && <Icono nombre="triangle-alert" />}
                                Conversaciones
                            </strong>
                            <span>{uso.detalle}</span>
                            <small>{uso.nota}</small>
                        </span>
                    </button>
                )}
                {pasadas !== null && <Pasadas cifra={pasadas} total={atendidas} desde={estado.handed_to_team_since ?? null} onIr={verConversaciones ? () => { navegar('/buzon'); } : null} />}
                </div>
                )}
            </div>

            {deshacer !== null && (
                <Deshacer
                    key={deshacer.clave}
                    clave={deshacer.clave}
                    movil={movil}
                    icono="power"
                    texto={deshacer.encendido ? 'Asistente apagado: los mensajes van al buzón.' : 'Asistente encendido.'}
                    onDeshacer={() => {
                        const volver = deshacer.encendido;
                        setDeshacer(null);
                        void encender(volver, false);
                    }}
                    onFin={() => { setDeshacer(null); }}
                />
            )}

            <div className="rv-en-barra rv-as-barra">
                <Segmentos
                    etiqueta="Apartados del asistente"
                    movil={movil}
                    items={APARTADOS.map((a) => ({ etiqueta: a.etiqueta, corta: a.corta, icono: a.icono, cifra: cifras[a.valor] ?? null }))}
                    valor={APARTADOS.findIndex((a) => a.valor === apartado)}
                    onCambio={(i) => {
                        const a = APARTADOS[i];
                        if (a !== undefined) {
                            irA(a.valor);
                        }
                    }}
                />
            </div>

            {soloLectura && (
                <p className="rv-as-nota rv-as-nota--gris" role="note">
                    <span>
                        <Icono nombre="lock" />
                        <span>{SOLO_LECTURA}</span>
                    </span>
                </p>
            )}

            <Apartado key={apartado} {...props} datos={datos} />

            <Ventana
                abierta={sinVersion}
                titulo="Publica antes de encenderlo"
                texto="Tu asistente todavía no tiene ninguna versión publicada. Pruébalo y publícalo; después podrás encenderlo."
                cancelar="Ahora no"
                accion="Ir a Probar y publicar"
                accionIcono="arrow-right"
                movil={movil}
                onAccion={() => {
                    setSinVersion(false);
                    irA('probar');
                }}
                onCerrar={() => { setSinVersion(false); }}
            />
        </div>
    );
}

/**
 * «Pasadas al equipo» (11.5, G718, G753): el porcentaje de las conversaciones que atendió el asistente este mes y que pasó al
 * buzón porque no supo o no debía resolverlas, con las dos cifras debajo («13 de 100»). Sin el total, solo el número. Con
 * permiso para ver conversaciones, lleva al Buzón.
 */
function Pasadas({ cifra, total, desde, onIr }: { cifra: number; total: number | null; desde: string | null; onIr: (() => void) | null }) {
    const porcentaje = total === null ? null : total === 0 ? 0 : Math.round((cifra / total) * 100);
    const conversaciones = (n: number) => (n === 1 ? '1 conversación' : `${n.toLocaleString('es-ES')} conversaciones`);
    const linea = total === null ? `${conversaciones(cifra)} este mes` : `${cifra.toLocaleString('es-ES')} de ${conversaciones(total)} este mes`;
    const resumen = porcentaje === null ? linea : `${String(porcentaje)} % este mes (${linea.replace(' este mes', '')})`;
    const contenido = (
        <>
            <span className="rv-as-cifra" aria-hidden="true">
                {porcentaje === null ? cifra.toLocaleString('es-ES') : porcentaje}
                {porcentaje !== null && <small className="rv-as-cifra__unidad">%</small>}
            </span>
            <span className="rv-as-uso__texto">
                <strong>Pasadas al equipo</strong>
                <span>{linea}</span>
                <small>{`Las que no supo o no debía resolver${desde === null ? '' : `, desde el ${new Date(desde).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', '')}`}.`}</small>
            </span>
        </>
    );
    return onIr === null ? (
        <section className="rv-as-tarjeta rv-as-uso rv-as-uso--fija" aria-label={`Pasadas al equipo: ${resumen}`}>{contenido}</section>
    ) : (
        <button type="button" className="rv-as-tarjeta rv-as-uso" aria-label={`Pasadas al equipo: ${resumen}. Ver en el Buzón`} onClick={onIr}>
            {contenido}
        </button>
    );
}

/** RV Anillo del consumo (rojo al 90 %). */
function Anillo({ porcentaje }: { porcentaje: number }) {
    const r = 30;
    const c = 2 * Math.PI * r;
    return (
        <span className="rv-as-anillo" aria-hidden="true">
            <svg viewBox="0 0 72 72" width="72" height="72">
                <circle cx="36" cy="36" r={r} fill="none" stroke="var(--rv-paper)" strokeWidth="8" />
                <circle cx="36" cy="36" r={r} fill="none" stroke={porcentaje >= 90 ? 'var(--rv-red)' : 'var(--rv-ink)'} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${String((c * Math.min(porcentaje, 100)) / 100)} ${String(c)}`} transform="rotate(-90 36 36)" />
            </svg>
            <span>
                <strong>{porcentaje}</strong>
                <small>%</small>
            </span>
        </span>
    );
}
